import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';

// Authentication
import { AuthProvider } from './contexts/AuthContext';
import NotificationProvider from './contexts/NotificationProvider';
import { FontSizeProvider } from './contexts/FontSizeContext';
import PrivacyMaskProvider from './contexts/PrivacyMaskContext';
import { PaperModeProvider, usePaperMode } from './contexts/PaperModeContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Layout components
import Layout from './components/Layout';

// Pages
import Dashboard from './pages/Dashboard';
import StockAnalysis from './pages/StockAnalysis';
import StockSummary from './pages/StockSummary';
import TaxProjection from './pages/TaxProjection';
import Scanner from './pages/Scanner';
import Orders from './pages/Orders';
import Accounts from './pages/Accounts';
import AccountSummary from './pages/AccountSummary';
import Transactions from './pages/Transactions';
import FieldMappings from './pages/FieldMappings';
import UserProfile from './pages/UserProfile';
import SyncLogs from './pages/SyncLogs';
import RobinhoodLogin from './pages/RobinhoodLogin';
import RobinhoodPositions from './pages/RobinhoodPositions';
import TradierMcpTest from './pages/TradierMcpTest';
import EarningsUpdate from './pages/EarningsUpdate';
import Maintenance from './pages/Maintenance';
import OptionOrderTest from './pages/OptionOrderTest';
import EquityOrderTest from './pages/EquityOrderTest';
import RobinhoodOrderTest from './pages/RobinhoodOrderTest';
import RobinhoodFeatureTest from './pages/RobinhoodFeatureTest';
import QuickTrade from './pages/QuickTrade';
import OrderHistory from './pages/OrderHistory';
import ApiTokens from './pages/ApiTokens';
import WebhookAlerts from './pages/WebhookAlerts';
import PaperTrading from './pages/PaperTrading';
import AdminUsers from './pages/AdminUsers';
import AdminApiMetrics from './pages/AdminApiMetrics';
import StrategyBuilder from './pages/StrategyBuilder';
import ReconIssues from './pages/ReconIssues';
import CopyTrading from './pages/CopyTrading';
import Chains from './pages/Chains';
import FeatureSplash from './components/FeatureSplash';



// Wraps a page component and redirects to Dashboard when paper mode is active
const PaperModeGuard = ({ children }) => {
  const { paperMode } = usePaperMode();
  if (paperMode) return <Navigate to="/" replace />;
  return children;
};

function AppInner() {
  const { theme } = useThemeMode();
  return (
    <ThemeProvider theme={theme}>
      <FontSizeProvider>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ProtectedRoute>
            <FeatureSplash />
            <NotificationProvider>
              <PrivacyMaskProvider>
              <PaperModeProvider>
              <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/stock-analysis" element={<StockAnalysis />} />
                <Route path="/stock-analysis/:symbol" element={<StockAnalysis />} />
                <Route path="/stock-summary" element={<StockSummary />} />
                <Route path="/tax-projection" element={<TaxProjection />} />
                <Route path="/scanner" element={<Scanner />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/accounts" element={<PaperModeGuard><Accounts /></PaperModeGuard>} />
                <Route path="/account-summary" element={<PaperModeGuard><AccountSummary /></PaperModeGuard>} />
                <Route path="/transactions" element={<PaperModeGuard><Transactions /></PaperModeGuard>} />
                <Route path="/user-profile" element={<UserProfile />} />
                <Route path="/field-mappings" element={<PaperModeGuard><FieldMappings /></PaperModeGuard>} />
                <Route path="/tradier-config" element={<Navigate to="/user-profile" replace />} />
                <Route path="/schwab-config" element={<Navigate to="/user-profile" replace />} />
                <Route path="/discord-settings" element={<Navigate to="/user-profile" replace />} />
                <Route path="/notification-settings" element={<Navigate to="/user-profile" replace />} />
                <Route path="/sync-logs" element={<SyncLogs />} />
                <Route path="/robinhood-login" element={<RobinhoodLogin />} />
                <Route path="/robinhood-positions" element={<RobinhoodPositions />} />
                <Route path="/tradier-mcp-test" element={<TradierMcpTest />} />
                <Route path="/earnings-update" element={<EarningsUpdate />} />
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/recon-issues" element={<ReconIssues />} />
                <Route path="/copy-trading" element={<CopyTrading />} />
                <Route path="/chains" element={<Chains />} />
                <Route path="/option-order-test" element={<OptionOrderTest />} />
                <Route path="/equity-order-test" element={<EquityOrderTest />} />
                <Route path="/robinhood-order-test" element={<RobinhoodOrderTest />} />
                <Route path="/robinhood-feature-test" element={<RobinhoodFeatureTest />} />
                <Route path="/quick-trade" element={<QuickTrade />} />
                <Route path="/order-history" element={<OrderHistory />} />
                <Route path="/api-tokens" element={<ApiTokens />} />
                <Route path="/webhook-alerts" element={<WebhookAlerts />} />
                <Route path="/paper-trading" element={<PaperTrading />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/api-usage" element={<AdminApiMetrics />} />
                <Route path="/strategy-builder" element={<StrategyBuilder />} />
              </Routes>
              </Layout>
              </PaperModeProvider>
              </PrivacyMaskProvider>
            </NotificationProvider>
          </ProtectedRoute>
        </Router>
      </AuthProvider>
      </FontSizeProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppInner />
    </ThemeModeProvider>
  );
}

export default App;
